import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import {
  getLiquidationSettlementAmount,
  getLiquidationSettlementLabel,
} from "../../utils/liquidationSettlement";
import { API_BASE_URL, authFetch } from "../../config/api";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getUnliquidatedAmount = (report = {}) =>
  (report.actual_cost_rows || []).reduce((sum, row) => {
    if (!row?.is_unliquidated) return sum;
    return sum + Number(row.amount || 0);
  }, 0);

const getDocumentKind = (doc = {}) => {
  const fileName = String(doc.file_name || doc.file_path || "").toLowerCase();
  if (fileName.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName)) return "image";
  return "other";
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const LiquidationUser127Approvals = () => {
  useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState("");
  const [activeRow, setActiveRow] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/liquidations`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) {
        throw new Error(json.message || "Failed to load liquidation approvals");
      }
      setRows(json.data || []);
    } catch (err) {
      setError(err.message || "Failed to load liquidation approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const pendingRows = useMemo(() => [], []);

  const approveRow = async () => {};

  const activeReport = activeRow?.liquidation_report || {};
  const activeDocuments = activeRow?.liquidation_documents || [];
  const approvalBadge =
    activeReport.approver_signature_url || activeReport.approved_at
      ? "Approved"
      : "No separate approval";

  return (
    <div className="min-h-full bg-slate-50 p-6 lg:p-8 font-poppins">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-[2rem] border border-white/60 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Liquidation
              </p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">
                Liquidation Approval Queue Deprecated
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                Separate liquidation approval by user 127 is no longer used. Team leader approval
                now finalizes the liquidation.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Pending</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">{pendingRows.length}</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Approver</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">Removed</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Accessible</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">Yes</div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search RFP, payee, job order, or team leader..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none ring-0 transition focus:border-blue-400"
              />
            </div>
            <button
              type="button"
              onClick={loadRows}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <i className="fa-solid fa-rotate-right" />
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-[2rem] border border-white/60 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Pending approvals</h2>
              <p className="text-sm text-slate-600">
                This queue is kept only for reference and should remain empty.
              </p>
            </div>
            <div className="text-sm text-slate-500">
              {pendingRows.length} item{pendingRows.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-slate-500">Loading pending approvals...</div>
          ) : pendingRows.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <th className="px-6 py-4">RFP No</th>
                    <th className="px-6 py-4">Payee</th>
                    <th className="px-6 py-4">Job Order</th>
                    <th className="px-6 py-4">Team Leader</th>
                    <th className="px-6 py-4 text-right">Status</th>
                    <th className="px-6 py-4">Report Date</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {pendingRows.map((row) => {
                    const report = row.liquidation_report || {};
                    const liquidation = row.liquidation || {};
                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setActiveRow(row)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setActiveRow(row);
                          }
                        }}
                      >
                        <td className="px-6 py-4 font-semibold text-blue-700">{row.rfp_no || "—"}</td>
                        <td className="px-6 py-4 text-slate-900">{row.payee || "—"}</td>
                        <td className="px-6 py-4 text-slate-600">{row.job_order || "—"}</td>
                        <td className="px-6 py-4 text-slate-700">
                          <div className="font-medium">{liquidation.team_leader_name || report.team_leader_name || "—"}</div>
                          <div className="text-xs text-slate-500">
                            {Number(liquidation.team_leader_user_id || report.team_leader_user_id || 0)
                              ? `User ID ${Number(liquidation.team_leader_user_id || report.team_leader_user_id)}`
                              : "No team leader selected"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                            Pending approval
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {formatDateTime(report.report_date_created || report.updated_at || row.updated_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActiveRow(row);
                            }}
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
                          >
                            <i className="fa-solid fa-eye" />
                            Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No liquidations are waiting for user 127 approval because team leader approval now finalizes the liquidation.
            </div>
          )}
        </div>
      </div>

      {activeRow
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/55 px-4">
              <div className="absolute inset-0" role="presentation" onClick={() => !savingId && setActiveRow(null)} />
              <div className="relative z-10 flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                <div className="shrink-0 border-b border-slate-100 p-6 lg:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Liquidation review
                      </p>
                      <h3 className="mt-2 text-2xl font-bold text-slate-900">
                        {activeRow.rfp_no || "Liquidation details"}
                      </h3>
                      <p className="mt-1 text-sm text-slate-600">
                        {activeRow.payee || "—"} | {activeRow.job_order || "No job order"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => !savingId && setActiveRow(null)}
                      className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Close modal"
                      disabled={savingId === String(activeRow.id)}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                  <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700">
                    <div className="font-semibold text-slate-900">
                      {approvalBadge}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      The approver signature appears only after user 127 approves this liquidation.
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 lg:p-8">
                  <div className="mx-auto max-w-5xl space-y-6">
                    <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="text-center">
                        <h4 className="text-3xl font-bold text-slate-900">Liquidation Report</h4>
                        <p className="mt-1 text-sm font-semibold uppercase tracking-[0.16em] text-slate-600">
                          Brokerage Department Liquidation Report
                        </p>
                      </div>

                      <div className="mt-6 grid gap-3 text-sm lg:grid-cols-2">
                        <div><span className="font-semibold">Date:</span> {formatDateTime(activeReport.report_date_created || activeReport.created_at || activeRow.updated_at)}</div>
                        <div><span className="font-semibold">Consignee:</span> {activeReport.consignee || activeRow.payee || "—"}</div>
                        <div><span className="font-semibold">JO:</span> {activeRow.job_order || activeReport.job_order || "—"}</div>
                        <div><span className="font-semibold">Tracking No:</span> {activeReport.tracking_no || "—"}</div>
                        <div className="lg:col-span-2"><span className="font-semibold">Shipment Details:</span> {activeReport.shipment_details || activeRow.shipment_details || "—"}</div>
                        <div><span className="font-semibold">Dock:</span> {activeReport.dock || "—"}</div>
                        <div><span className="font-semibold">P.O No:</span> {activeReport.po_no || "—"}</div>
                        <div><span className="font-semibold">Reference No:</span> {activeReport.reference_no || "—"}</div>
                        <div><span className="font-semibold">Delivery Date:</span> {activeReport.delivery_date || "—"}</div>
                      </div>

                      <div className="mt-6 space-y-4">
                        <div>
                          <div className="mb-2 bg-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-white">
                            Cost Incurred for Shipment
                          </div>
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="bg-slate-100 text-left uppercase tracking-[0.08em] text-slate-600">
                                <th className="border border-slate-300 px-3 py-2">Particulars</th>
                                <th className="border border-slate-300 px-3 py-2">OR No.</th>
                                <th className="border border-slate-300 px-3 py-2 text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(activeReport.actual_cost_rows || []).length ? (
                                activeReport.actual_cost_rows.map((row) => (
                                  <tr key={row.id || `${row.particulars}-${row.or_no}`}>
                                    <td className="border border-slate-300 px-3 py-2">
                                      <div className="font-medium">{row.particulars || "—"}</div>
                                      {row.is_unliquidated ? (
                                        <div className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-amber-700 ring-1 ring-amber-200">
                                          Unliquidated
                                        </div>
                                      ) : null}
                                    </td>
                                    <td className="border border-slate-300 px-3 py-2">{row.or_no || "—"}</td>
                                    <td className="border border-slate-300 px-3 py-2 text-right">{money(row.amount)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border border-slate-300 px-3 py-4 text-center text-slate-500" colSpan={3}>
                                    No cost rows available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          <div className="mt-2 flex justify-end">
                            <div className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900">
                              TOTAL ACTUAL COST INCURRED: ₱{money(activeReport.total_actual_cost_incurred)}
                            </div>
                          </div>
                          {getUnliquidatedAmount(activeReport) > 0 ? (
                            <div className="mt-2 flex justify-end">
                              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                                UNLIQUIDATED AMOUNT: ₱{money(getUnliquidatedAmount(activeReport))}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <div className="mb-2 bg-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-white">
                            Advances Table
                          </div>
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="bg-slate-100 text-left uppercase tracking-[0.08em] text-slate-600">
                                <th className="border border-slate-300 px-3 py-2">Particulars</th>
                                <th className="border border-slate-300 px-3 py-2">OR No.</th>
                                <th className="border border-slate-300 px-3 py-2 text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(activeReport.advance_rows || []).length ? (
                                activeReport.advance_rows.map((row) => (
                                  <tr key={row.id || `${row.particulars}-${row.or_no}`}>
                                    <td className="border border-slate-300 px-3 py-2">{row.particulars || "—"}</td>
                                    <td className="border border-slate-300 px-3 py-2">{row.or_no || "—"}</td>
                                    <td className="border border-slate-300 px-3 py-2 text-right">{money(row.amount)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td className="border border-slate-300 px-3 py-4 text-center text-slate-500" colSpan={3}>
                                    No advance rows available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          <div className="mt-2 flex justify-end">
                            <div className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900">
                              TOTAL ADVANCES: ₱{money(activeReport.total_advances)}
                            </div>
                          </div>
                          <div className="mt-3 flex justify-end">
                            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
                              Liquidation: {getLiquidationSettlementLabel(activeReport.total_reimbursement_return)} ₱{money(getLiquidationSettlementAmount(activeReport.total_reimbursement_return))}
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
                            <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Prepared by</div>
                            <div className="mt-3 flex h-14 items-center justify-center">
                              {activeReport.preparer_signature_url ? (
                                <img src={activeReport.preparer_signature_url} alt="Preparer signature" className="max-h-14 object-contain" />
                              ) : (
                                <span className="text-xs text-slate-400">Pending</span>
                              )}
                            </div>
                            <div className="mt-2 border-t border-slate-300 pt-2 text-sm font-semibold text-slate-900">
                              {activeReport.preparer_name || activeReport.created_by_name || "—"}
                            </div>
                            <div className="text-xs text-slate-500">Preparer</div>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
                            <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Noted by</div>
                            <div className="mt-3 flex h-14 items-center justify-center">
                              {activeReport.team_leader_approved_signature_url ? (
                                <img
                                  src={activeReport.team_leader_approved_signature_url}
                                  alt="Team leader signature"
                                  className="max-h-14 object-contain"
                                />
                              ) : (
                                <span className="text-xs text-slate-400">Pending</span>
                              )}
                            </div>
                            <div className="mt-2 border-t border-slate-300 pt-2 text-sm font-semibold text-slate-900">
                              {activeReport.team_leader_approved_name || activeReport.team_leader_name || "—"}
                            </div>
                            <div className="text-xs text-slate-500">Brokerage Team Leader</div>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
                            <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Approved by</div>
                            <div className="mt-3 flex h-14 items-center justify-center">
                              {activeReport.approver_signature_url ? (
                                <img src={activeReport.approver_signature_url} alt="Approver signature" className="max-h-14 object-contain" />
                              ) : (
                                <span className="text-xs text-slate-400">Awaiting approval</span>
                              )}
                            </div>
                            <div className="mt-2 border-t border-slate-300 pt-2 text-sm font-semibold text-slate-900">
                              {activeReport.approver_name || "—"}
                            </div>
                            <div className="text-xs text-slate-500">{approvalBadge}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Attached documents
                        </h4>
                        <span className="text-xs text-slate-500">
                          {activeDocuments.length} file{activeDocuments.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="mt-3 space-y-3">
                        {activeDocuments.length ? (
                          activeDocuments.map((doc) => {
                            const kind = getDocumentKind(doc);
                            return (
                              <div key={doc.id || doc.file_path} className="rounded-2xl border border-white bg-white p-3 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate font-semibold text-slate-900">
                                      {doc.file_name || "Unnamed file"}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {doc.doc_type || "document"} | {doc.uploaded_by_name || "—"} | {formatDateTime(doc.uploaded_at)}
                                    </div>
                                  </div>
                                  {doc.file_url ? (
                                    <a
                                      href={doc.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                                    >
                                      Open
                                    </a>
                                  ) : null}
                                </div>
                                <div className="mt-3 overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
                                  {kind === "image" && doc.file_url ? (
                                    <img
                                      src={doc.file_url}
                                      alt={doc.file_name || "Liquidation document"}
                                      className="h-56 w-full object-contain bg-white"
                                    />
                                  ) : kind === "pdf" && doc.file_url ? (
                                    <iframe title={doc.file_name || "Liquidation document"} src={doc.file_url} className="h-56 w-full" />
                                  ) : (
                                    <div className="flex h-56 items-center justify-center px-4 text-center text-sm text-slate-500">
                                      Preview not available. Open the file to view it.
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                            No liquidation documents uploaded yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="shrink-0 border-t border-slate-100 bg-white p-6 lg:p-8">
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => !savingId && setActiveRow(null)}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={savingId === String(activeRow.id)}
                    >
                      Close
                    </button>
                  </div>
                  <p className="mt-3 text-xs font-medium text-slate-500">
                    This page is now read-only because team leader approval finalizes the liquidation.
                  </p>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {successMessage
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/60 px-4">
              <div
                className="absolute inset-0"
                role="presentation"
                onClick={() => setSuccessMessage("")}
              />
              <div className="relative z-10 w-full max-w-md overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                <div className="flex items-start gap-4 border-b border-emerald-100 bg-emerald-50 p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <i className="fa-solid fa-circle-check text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                      Success
                    </p>
                    <h3 className="mt-1 text-xl font-bold text-slate-900">
                      Liquidation approved
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">{successMessage}</p>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-sm text-slate-600">
                    The liquidation has been moved out of the pending queue and the approver
                    signature is now available on the report.
                  </p>
                  <div className="mt-5 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setSuccessMessage("")}
                      className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default LiquidationUser127Approvals;
